import { zodResolver } from '@hookform/resolvers/zod';
import { Camera } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { COUNTRIES } from '@/lib/countries';
import { MemberSelect } from '@/features/members';
import { useSession } from '@/stores/session';
import { EMPLOYEE_STATUSES, EMPLOYMENT_TYPES } from '../constants';
import {
  createDepartmentOption,
  resolveDepartmentLabel,
  searchDepartments,
} from '../departmentSelect';
import { useDepartment } from '../queries';
import { resolveEmployeeLabel, searchEmployees } from '../select';
import type { EmployeeInput } from '../types';

/** Roles allowed to link an employee to a login account — mirrors the backend
 *  HR_MANAGE_ROLES gate on the employee write endpoint. */
const USER_LINK_ROLES = new Set(['super_admin', 'founder', 'hr']);

const schema = z.object({
  user: z.string(),
  first_name: z.string().trim().min(1, 'First name is required').max(100),
  last_name: z.string().max(100),
  code: z.string().max(16),
  email: z.string().email('Enter a valid email').or(z.literal('')),
  personal_email: z.string().email('Enter a valid email').or(z.literal('')),
  phone_number: z.string().max(32),
  city: z.string().max(120),
  address: z.string(),
  legal_name: z.string().max(255),
  legal_name_local: z.string().max(255),
  address_local: z.string(),
  contract_number: z.string().max(100),
  contract_date: z.string(),
  employment_type: z.enum(['employee', 'contractor']),
  job_title: z.string().max(150),
  department: z.string(),
  manager: z.string(),
  hire_date: z.string(),
  trial_period_end: z.string(),
  end_date: z.string(),
  status: z.enum(['active', 'on_leave', 'terminated']),
  country: z.string().max(2, 'Use the 2-letter ISO code'),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'user', 'first_name', 'last_name', 'code', 'email', 'personal_email', 'phone_number',
  'city', 'address', 'legal_name', 'legal_name_local', 'address_local',
  'contract_number', 'contract_date', 'employment_type', 'job_title',
  'department', 'manager', 'hire_date', 'trial_period_end', 'end_date', 'status', 'country',
] as const;

interface EmployeeFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  /** Existing photo URL, shown as the preview until a new file is picked. */
  currentPhoto?: string | null;
  /** Name used for the avatar initials fallback. */
  currentName?: string;
  onSubmit: (values: EmployeeInput, photo: File | null) => Promise<unknown>;
  onCancel: () => void;
}

export function EmployeeForm({
  defaultValues,
  submitLabel,
  currentPhoto,
  currentName,
  onSubmit,
  onCancel,
}: EmployeeFormProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<File | null>(null);

  // Build an object URL for the locally-picked file and revoke it on change/unmount.
  const preview = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const previewSrc = preview ?? currentPhoto ?? undefined;

  function handlePhoto(e: React.ChangeEvent<HTMLInputElement>) {
    setPhoto(e.target.files?.[0] ?? null);
  }

  const role = useSession((s) => s.role);
  const canLinkUser = role != null && USER_LINK_ROLES.has(role);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      user: '',
      first_name: '',
      last_name: '',
      code: '',
      email: '',
      personal_email: '',
      phone_number: '',
      city: '',
      address: '',
      legal_name: '',
      legal_name_local: '',
      address_local: '',
      contract_number: '',
      contract_date: '',
      employment_type: 'employee',
      job_title: '',
      department: '',
      manager: '',
      hire_date: '',
      trial_period_end: '',
      end_date: '',
      status: 'active',
      country: '',
      ...defaultValues,
    },
  });

  // Suggest the employee code from name initials + the department's code
  // (e.g. "IC-HRD"). Fills while the field is empty or still holds our last
  // suggestion — never clobbers a code the admin typed, and never on an
  // already-set code (edit).
  const watchedFirst = useWatch({ control: form.control, name: 'first_name' });
  const watchedLast = useWatch({ control: form.control, name: 'last_name' });
  const watchedDept = useWatch({ control: form.control, name: 'department' });
  const { data: department } = useDepartment(Number(watchedDept) || 0);
  const suggestedCode = useMemo(() => {
    const initials =
      `${watchedFirst.trim()[0] ?? ''}${watchedLast.trim()[0] ?? ''}`.toUpperCase();
    if (!initials) return '';
    const deptCode = (department?.code ?? '').trim().toUpperCase();
    return deptCode ? `${initials}-${deptCode}` : initials;
  }, [watchedFirst, watchedLast, department]);

  const autoFilledCode = useRef('');
  useEffect(() => {
    if (!suggestedCode) return;
    const current = form.getValues('code');
    if (!current.trim() || current === autoFilledCode.current) {
      form.setValue('code', suggestedCode, { shouldValidate: true });
      autoFilledCode.current = suggestedCode;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedCode]);

  async function handleSubmit(values: FormValues) {
    const input: EmployeeInput = {
      first_name: values.first_name,
      last_name: values.last_name,
      code: values.code,
      email: values.email,
      personal_email: values.personal_email,
      phone_number: values.phone_number,
      city: values.city,
      address: values.address,
      legal_name: values.legal_name,
      legal_name_local: values.legal_name_local,
      address_local: values.address_local,
      contract_number: values.contract_number,
      contract_date: values.contract_date || null,
      employment_type: values.employment_type,
      job_title: values.job_title,
      department: values.department ? Number(values.department) : null,
      manager: values.manager ? Number(values.manager) : null,
      hire_date: values.hire_date || null,
      trial_period_end: values.trial_period_end || null,
      end_date: values.end_date || null,
      status: values.status,
      country: values.country.trim().toUpperCase(),
    };
    // Only HR-manage roles can (re)link the account; otherwise leave it untouched.
    if (canLinkUser) {
      input.user = values.user ? Number(values.user) : null;
    }
    try {
      await onSubmit(input, photo);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <FormSection variant="card" title="Identity" description="Who is this person?">
          <div className="flex items-center gap-4">
            <Avatar className="size-16">
              {previewSrc ? <AvatarImage src={previewSrc} alt="" /> : null}
              <AvatarFallback className="text-xl">{initials(currentName)}</AvatarFallback>
            </Avatar>
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                <Camera /> {photo ? 'Change photo' : 'Upload photo'}
              </Button>
              {photo ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-2"
                  onClick={() => {
                    setPhoto(null);
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                >
                  Remove
                </Button>
              ) : null}
              <p className="text-muted-foreground mt-1 text-xs">PNG or JPG, square works best.</p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhoto}
              />
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input placeholder="Dana" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input placeholder="Scully" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Code
                    <InfoHint text="First-name initial + last-name initial + department code (e.g. IC-HRD). Used in staff-invoice numbers → INV-IC-HRD-2026-001." />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="IC-HRD" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid items-start gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="dana@company.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {canLinkUser ? (
              <FormField
                control={form.control}
                name="user"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      Login account
                      <InfoHint text="Link to a user so they can sign in to their own cabinet." />
                    </FormLabel>
                    <FormControl>
                      <MemberSelect
                        queryKey="employee-user-picker"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="No linked account"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
          </div>
        </FormSection>

        <FormSection variant="card" title="Contact" description="How to reach this person.">
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="personal_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Personal email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="dana@gmail.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 555 123 4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid items-start gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input placeholder="Berlin" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Textarea rows={2} placeholder="Street, number, postal code…" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection
          variant="card"
          title="Invoicing identity"
          description="Printed on the contractor (FOP/PE) staff invoice — the employee is the supplier. Local-language fields fill the invoice's second column; leave blank for an English-only document."
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="legal_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Legal name
                    <InfoHint text="Full registered designation, e.g. “Natural person-entrepreneur Rybonka Stanislav Mykolaiovych”." />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="legal_name_local"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Legal name (local language)</FormLabel>
                  <FormControl>
                    <Input placeholder="ФОП Прізвище Ім’я По батькові" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="address_local"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address (local language)</FormLabel>
                <FormControl>
                  <Textarea rows={2} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="contract_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Contract number
                    <InfoHint text="The services contract the invoice bills under — banks match cross-border transfers against it." />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="SS1083/2026R" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contract_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contract date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Employment" description="Role, reporting line, and dates.">
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="employment_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employment type</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {EMPLOYMENT_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {EMPLOYEE_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="job_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job title</FormLabel>
                  <FormControl>
                    <Input placeholder="Senior Engineer" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Department</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="department-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchDepartments}
                      resolveLabel={resolveDepartmentLabel}
                      onCreate={createDepartmentOption}
                      placeholder="No department"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="manager"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  Manager
                  <InfoHint text="Who this person reports to." />
                </FormLabel>
                <FormControl>
                  <EntitySelect
                    queryKey="employee-picker"
                    value={field.value}
                    onChange={field.onChange}
                    search={searchEmployees}
                    resolveLabel={resolveEmployeeLabel}
                    placeholder="No manager"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid items-start gap-6 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="hire_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hire date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="trial_period_end"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Trial period end
                    <InfoHint text="End of probation, if any." />
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    End date
                    <InfoHint text="Leave blank if still employed." />
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}

function initials(name?: string): string {
  return (name ?? '')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
